import Vue from 'vue';
import Vuex from 'vuex';
import commonJs from '../common.js';
import createPersistedState from 'vuex-persistedstate';

// Vue.config.devtools should be set to TRUE only for debugging and testing locally
// Allows to debug Vue components and Vuex (store) in browser (Chrome plugin: Vue.js devtools)
// By default should be set to FALSE
Vue.config.devtools = true;
Vue.use(Vuex);

export const store = new Vuex.Store({
    plugins: [createPersistedState()],
    state: {
        isAuthenticated: false,
        currentPageLayout: 'SimpleLayout',
        supportInformation: [],
        //I defined this out, mostly for intellisense and to avoid continual null checks on the customer login object.
        //This object will otherwise be completely replaced by the SET_CUSTOMERINFO, and defining it is not necessary in JQuery.
        customerInfo: {
            CustomerFullName: "",
            AccountNumber: "",
            SelectedAccount: '',
            CustomerAvailableAccounts: {
                CustomerAccountInfoID: -1,
                CustomerID: -1,
                UtilityAccountNumber: '',
                UtilityCustomerNumber: ''
            },
            DisplayAccountsDropdown: false,
            SelectedServiceLocation: '', 
            SelectedServiceLocationAddress: "None",
            DisplayUsageReport: false,
            CustomerAvailableServiceLocations: {
                ServiceLocationID: -1,
                Address1: "None",
                Address2: "",
                City: "",
                State: "",
                Zip: "",
                County: "",
                ClientServiceLocationID: "",
                LoginResults: -1
            },
            DisplayServiceLocationDropdown: false,
            IsPrePay: true,
            DaysInBillingPeriod: 0,
            TotalDue: 0,
            TotalDueLabelText: "",
            HeaderImage: '',
            FooterImage: '',
            EmailAsUsername: false,
            Username: '',
            NavigationMenuLinks: [],
            TimeoutMessage: '',            
            //StateList is the 50 states and their abbreviations.
            StateList: [],
            //Language has to be kept in store to eliminate a reset
            Language: '',
            DisplayHeaderAccountInformation: false,
            RepThemeSettings: '',
            RenderPagesInIframe: false,
            SelectedPremiseID: -1,
            HomepageTimer: 900000, // 15 minutes
            RedirectLinkForLogin: '',
            HeaderBalanceData: '',
            IsCSR: false,
            IsCSRReadOnly: false,
            LowBalanceDaysDescriptionText: '',
            CustomerLoginBySSOOnly: false,
            DisplayCopyrightAndPrivacy: false,
            DisplayLanguages: false,
            IsCustomerHomeMenuEnabled: false,
            TextMessageDisclaimer: '',
            SessionEndedMessageForSSOCustomer: ''
        },
        //Last Thermostat ID is primarily used to keep the state of the thermostat.
        LastThermostatID: 0,
        //List of all reports available for customers.  Stored here mostly so they don't have to be retrieved again should the user return to this page
        //, but also because things like "Printer Report" wants the current filtered row and this is the best way to do that.
        //Unlike above, I'm not rendering this out; since it is huge and an array.  For details, see the report controller.
        reportData: {
            reports: [],
            filteredRows: [],
            Columns: [],
            selectedReport: null,
            errorMsg: '',
            TopAddHTML: ''
        },
        //The return objects have this
        //public int PremiseId;
        //public string Address;
        //public int PoolId;
        premises: [],
        //A message to show on the login page.  Used if you redirect; can be used for "Forgot Password", "Logged out", etc.
        loginMessage: '',
        passwordRules: '',
        showHeaderAccountInformation: false,
        isDesktop: true,
        //I put the start /end date in the store.  This is currently for reports, so if they leave and come back I remember what date they wanted for their reports.
        startDate: null,
        endDate: null,
        selectedDateRange: 0,
        repThemeLoaded: false,
        billingSummaryData: '',
        customerInfoLoaded: false,
        servicePointIntervalId: '',
        sessionTimeout: 15,
        //EETips information, so we do not load.
        EETips: {
            HasLoaded: false,
            Tips: [],
            BackgroundColor: '',
            TitleStyle: '', 
            TeaserStyle: '',
            ItemDescriptionStyle: ''
        }

    },
    mutations: {
        setAuthentication (state, payload) {
            state.isAuthenticated = payload;
        },
        setCurrentPageLayout (state, payload) {
            state.currentPageLayout = payload;
        },
        setCustomerInfo (state, payload) {
            state.customerInfo = payload;

            // At this point, we get data for Rep Theme as well
            state.repThemeLoaded = true;

            state.customerInfoLoaded = true;
        },
        setSelectedReport (state, payload) {
            state.reportData.selectedReport = payload;
        },
        setTopAddHTML (state, payload) {
            state.reportData.TopAddHTML = payload;
        },
        setReports (state, payload) {
            state.reportData.reports = payload;
        },
        setReportData (state, payload) {
            state.reportData = payload;
        },
        setFilteredRows (state, payload) {
            state.reportData.filteredRows = payload;
        },
        setColumns (state, payload) {
            state.reportData.Columns = payload;
        },
        setSupportInformation (state, payload) {
            state.supportInformation = payload;
        },
        setLoginMessage (state, payload) {
            state.loginMessage = payload;
        },
        setHeaderImage (state, payload) {
            state.customerInfo.HeaderImage = payload;
        },
        setFooterImage (state, payload) {
            state.customerInfo.FooterImage = payload;
        },
        setStateList (state, payload) {
            state.StateList = payload;
        },
        setNavigationMenuLinks(state, payload) {           
            state.customerInfo.NavigationMenuLinks = payload;
        },
        setPasswordRules (state, payload) {
            state.passwordRules = payload;
        },
        setShowHeaderAccountInformation (state, payload) {
            state.showHeaderAccountInformation = payload;
        },
        setIsDesktop (state, payload) {
            state.isDesktop = payload;
        },
        setStartDate (state, payload) {
            state.startDate = payload;
        },
        setEndDate (state, payload) {
            state.endDate = payload;
        },
        setSelectedDateRange(state, payload) {
            state.selectedDateRange = payload;
        },
        setLanguage (state, payload) {
            state.Language = payload;
        },
        setRepThemeLoaded (state, payload) {
            state.repThemeLoaded = payload;
        },
        setLastThermostatID (state, payload) {
            state.LastThermostatID = payload;
        },
        setRepThemeSettings (state, payload) {
            state.customerInfo.repThemeSettings = payload;
        },
        setSelectedPremiseID (state, payload) {
            state.customerInfo.SelectedPremiseID = payload;
        },
        setPremises (state, payload) {
            state.premises = payload;
        },
        setBillingSummaryData (state, payload) {
            state.billingSummaryData = payload;

            // Update Header Balance data (customerInfo object) with the latest information
            state.customerInfo.HeaderBalanceData = state.billingSummaryData.BalanceSummaryData;
        },
        setCustomerLoginBySSOOnly (state, payload) {
            state.customerInfo.CustomerLoginBySSOOnly = payload;
        },
        setServicePointIntervalId(state, payload) {
            state.servicePointIntervalId = payload;
        },
        setSessionTimeout(state, payload) {
            state.sessionTimeout = payload;
        },
        setDisplayHeaderAccountInformation(state, payload) {
            state.customerInfo.DisplayHeaderAccountInformation = payload;
        },
        setIsCustomerHomeMenuEnabled(state, payload) {
            state.customerInfo.IsCustomerHomeMenuEnabled = payload;
        },
        setUsername(state, payload) {
            state.customerInfo.Username = payload;
        },
        setSessionEndedMessageForSSOCustomer(state, payload) {
            state.customerInfo.SessionEndedMessageForSSOCustomer = payload;
        },
        setEEtipsHasLoaded(state, payload) {
            state.EETips.HasLoaded = payload;
        },
        setEETips(state, payload) {
            state.EETips.Tips = payload;
        },
        setEETipBackgroundColor(state, payload) {
            state.EETips.BackgroundColor = payload;
        },
        setEETipsTitleStyle(state, payload) {
            state.EETips.TitleStyle = payload;
        },
        setEETipsTeaserStyle(state, payload) {
            state.EETips.TeaserStyle = payload;
        },
        setEETipsItemDescriptionStyle(state, payload) {
            state.EETips.ItemDescriptionStyle = payload;
        }
      
    },
    getters: {
        getIsAuthenticated (state) {
            return state.isAuthenticated;
        },
        getCurrentPageLayout (state) {
            return state.currentPageLayout;
        },
        getCurrentLocale (state) {
            return state.locale;
        },
        getCustomerInfo (state) {
            return state.customerInfo;
        },
        getDisplayUsageReport (state) {
            return state.customerInfo.DisplayUsageReport;
        },     
        getSelectedServiceLocation (state) {
            return state.customerInfo.SelectedServiceLocation;
        },
        getAccountNumber (state) {
            return state.customerInfo.AccountNumber;
        },
        getSupportInformation (state) {
            return state.supportInformation;
        },
        getReportData (state) {
            return state.reportData;
        },
        getLoginMessage (state) {
            return state.loginMessage;
        },
        getHeaderImage (state) {
            return state.customerInfo.HeaderImage;
        },
        getFooterImage (state) {
            return state.customerInfo.FooterImage;
        },
        getReports (state) {
            return state.reportData.reports;
        },
        getFilteredRows (state) {
            return state.reportData.filteredRows;
        },
        getColumns (state) {
            return state.reportData.Columns;
        },
        getTopAddHTML(state) {
            return state.reportData.TopAddHTML;
        },
        getSelectedReport(state) {
            return state.reportData.selectedReport;
        },
        getStateList (state) {
            return state.StateList;
        },
        getNavigationMenuLinks (state) {
            return state.customerInfo.NavigationMenuLinks;
        },
        getPasswordRules (state) {
            return state.passwordRules;
        },
        getEmailAsUsername (state) {
            return state.customerInfo.EmailAsUsername;
        },
        getCustomerUsername (state) {
            return state.customerInfo.Username;
        },
        getShowHeaderAccountInformation (state) {
            return state.showHeaderAccountInformation;
        },
        getIsDesktop (state) {
            return state.isDesktop;
        },
        getTimeoutMessage (state) {
            return state.customerInfo.TimeoutMessage;
        },
        getStartDate (state) {
            //option 4 is custom date range; otherwise we calculate.  These are static; I've looked
            //at options to make this flexible, but it doesn't feel like they have changed in a while.  Having said all of that, who knows in the future.
            if (state.selectedDateRange === 4) {
                if (state.startDate === null) {
                    return commonJs.subtractMonths(new Date(), 1);
                }
                else {
                    return state.startDate;
                }
            }
            else {
                switch (state.selectedDateRange) {
                    case 0: //Last month
                        return commonJs.subtractMonths(new Date(), 1);
                    case 1: //3 months ago
                        return commonJs.subtractMonths(new Date(), 3);
                    case 2:
                        return commonJs.subtractMonths(new Date(), 6);
                    case 3:
                        return commonJs.subtractMonths(new Date(), 9);
                }
            }
        },
        getEndDate (state) {
            return state.endDate === null || state.selectedDateRange !== 4 ? new Date() : state.endDate;
        },
        getSelectedDateRange (state) {
            return state.selectedDateRange;
        },
        getLanguage (state) {
            return state.Language;
        },
        getRepThemeLoaded (state) {
            return state.repThemeLoaded;
        },
        getLastThemostatID (state) {
            return state.LastThermostatID;
        },
        getDisplayHeaderAccountInformation (state) {
            return state.customerInfo.DisplayHeaderAccountInformation;
        },
        getRepThemeSettings (state) {
            return state.customerInfo.RepThemeSettings;
        },
        getRenderPagesInIframe (state) {
            return state.customerInfo.RenderPagesInIframe;
        },
        getPremises (state) {
            return state.premises;
        },
        getSelectedPremiseID (state) {
            return state.customerInfo.SelectedPremiseID; 
        },
        getHomepageTimer (state) {
            return state.customerInfo.HomepageTimer;
        },
        getAvailableServiceLocations (state) {
            return state.customerInfo.CustomerAvailableServiceLocations;
        },
        getRedirectLinkForLogin (state) {
            return state.customerInfo.RedirectLinkForLogin;
        },
        getBillingSummaryData (state) {
            return state.billingSummaryData;
        },
        getIsCSR (state) {
            return state.customerInfo.IsCSR;
        },
        getIsCSRReadOnly(state) {
            return state.customerInfo.IsCSRReadOnly;
        },
        getLowBalanceDaysDescriptionText (state) {
            return state.customerInfo.LowBalanceDaysDescriptionText;
        },
        getPageTitleFromRepThemeSettings (state) {
            return state.customerInfo.RepThemeSettings.PageTitle;
        },
        getCustomerLoginBySSOOnly (state) {
            return state.customerInfo.CustomerLoginBySSOOnly;
        },
        getCustomerInfoLoaded (state) {
            return state.customerInfoLoaded;
        },
        getDisplayCopyrightAndPrivacy(state) {
            return state.customerInfo.DisplayCopyrightAndPrivacy;
        },
        getDisplayLanguages(state) {
            return state.customerInfo.DisplayLanguages;
        },
        getServicePointIntervalId(state) {
            return state.servicePointIntervalId;
        },
        getSessionTimeout(state) {
            return state.sessionTimeout;
        },
        getTextMessageDisclaimer(state) {
            return state.customerInfo.TextMessageDisclaimer;
        },
        getIsCustomerHomeMenuEnabled(state) {
            return state.customerInfo.IsCustomerHomeMenuEnabled;
        },
        getSessionEndedMessageForSSOCustomer(state) {
            return state.customerInfo.SessionEndedMessageForSSOCustomer;
        },
        getEEtipsHasLoaded(state) {
            return state.EETips.HasLoaded;
        },
        getEETips(state) {
            return state.EETips.Tips;
        },
        getEETipBackgroundColor(state) {
            return state.EETips.BackgroundColor;
        },
        getEETipsTitleStyle(state) {
            return state.EETips.TitleStyle;
        },
        getEETipsTeaserStyle(state) {
            return state.EETips.TeaserStyle;
        },
        getEETipsItemDescriptionStyle(state) {
            return state.EETips.ItemDescriptionStyle;
        }
    },
    actions: {
        resetApp(store) {
            store.commit('setAuthentication', false);
            store.commit('setCurrentPageLayout');
            store.commit('setShowHeaderAccountInformation', false);
            store.commit('setIsDesktop');
            store.commit('setRepThemeLoaded', false);
            store.commit('setCustomerLoginBySSOOnly', false);
            store.state.customerInfo.NavigationMenuLinks = [];
            store.dispatch('resetCustomerInformationOnLogout');
            store.state.customerInfoLoaded = false;
            store.sessionTimeout = 15;
            store.commit('setSessionEndedMessageForSSOCustomer', '');
        },
        resetCustomerInformationOnLogout(store) {        
            store.state.customerInfo.AccountNumber = '';
            store.state.customerInfo.DaysRemaining = 0;
            store.state.customerInfo.DollarsRemaining = 0;
            store.state.customerInfo.SelectedAccount = '';
            store.state.customerInfo.CustomerAvailableAccounts = [];
            store.state.customerInfo.DisplayAccountsDropdown = false;
            store.state.customerInfo.CustomerAvailableServiceLocations = [];
            store.state.customerInfo.CustomerFullName = '';
            store.state.customerInfo.SelectedServiceLocation = '';
            store.state.customerInfo.SelectedServiceLocationAddress = '';
            store.state.customerInfo.DisplayServiceLocationDropdown = false;
            store.state.customerInfo.RenderPagesInIframe = false;
            store.state.customerInfo.IsCustomerHomeMenuEnabled = false;
            store.state.billingSummaryData = '';
            store.state.customerInfo.IsCSR = false;
        },
        resetReportData(store) {
            store.state.reportData.reports = [];
            store.state.reportData.errorMsg = '';
            store.state.reportData.TopAddHTML = ''
        }
    }
});